import type { ComponentType } from "react";
import type { LazyRouteFunction } from "react-router-dom";
import { RouteObject } from "react-router-dom";

import { handleDynamicImportError } from "@/helpers/errorHelpers";
import { ErrorPage } from "@/pages";

import SecureRouteWrapper from "./SecureRouteWrapper.tsx";

type RouteProtectionOptions = {
  requiresAdmin?: boolean;
  requiresInstitutionAdmin?: boolean;
};
type RouteComponent = ComponentType & { isUnprotected?: boolean };

export const routes: RouteObject[] = [
  {
    path: "/",
    lazy: createLazyRoute(import("../components").then((module) => ({ default: module.AppBaseLayout }))),
    children: [
      { path: "valuation_report", lazy: createLazyPagesRoute((module) => module.ValuationReportPage) },
      { path: "valuation_report/error", lazy: createLazyPagesRoute((module) => module.ValuationErrorPage) },
      { path: "test", lazy: createLazyPagesRoute((module) => module.TestPage) },
      { path: "", lazy: createLazyPagesRoute((module) => module.HomePage) },
      { path: "indexes", lazy: createLazyPagesRoute((module) => module.IndexesLandingPage) },
      { path: "landing", lazy: createLazyPagesRoute((module) => module.HomePage) },
      { path: "portfolio-analysis", lazy: createLazyPagesRoute((module) => module.PortfolioAnalysisPage) },
      { path: "playground", lazy: createLazyPagesRoute((module) => module.PlaygroundPage) },
      { path: "logout", lazy: createLazyPagesRoute((module) => module.LogoutPage) },
      { path: "outlier-shield", lazy: createLazyPagesRoute((module) => module.OutlierShieldPage) },
      { path: "oneclick", lazy: createLazyPagesRoute((module) => module.OneClickPage) },

      { path: "features", lazy: createLazyPagesRoute((module) => module.FeaturePage) },
      { path: "valuations", lazy: createLazyPagesRoute((module) => module.ValuationListPage) },
      { path: "valuations/new", lazy: createLazyPagesRoute((module) => module.ValuationNewPage) },
      { path: "valuations/:valuationId", lazy: createLazyPagesRoute((module) => module.ValuationDetailsPage) },
      {
        path: "valuations/:valuationId/analysis",
        lazy: createLazyPagesRoute((module) => module.ValuationAnalysisPage),
      },
      { path: "*", lazy: createLazyPagesRoute((module) => module.NotFoundPage) },
      {
        path: "settings",
        lazy: createLazyRoute(
          import("../pages/valuationPackage/settingsProfile/settingsShared/SettingsBasePage.tsx").then((m) => ({
            default: m.SettingsBasePage,
          })),
        ),
        children: [
          { path: "profile", lazy: createLazyPagesRoute((module) => module.SettingsProfilePage) },
          {
            path: "institution",
            lazy: createLazyPagesRoute((module) => module.SettingsInstitutionPage, {
              requiresInstitutionAdmin: true,
            }),
          },
          {
            path: "userlist",
            lazy: createLazyPagesRoute((module) => module.SettingsUsersPage, {
              requiresInstitutionAdmin: true,
            }),
          },
        ],
      },
    ],
  },
  {
    path: "/backoffice",
    lazy: createLazyRoute(
      import("../components").then((module) => ({ default: module.AppBaseLayout })),
      { requiresAdmin: true },
    ),
    children: [
      { path: "", lazy: createLazyPagesRoute((module) => module.InstitutionPage, { requiresAdmin: true }) },
      {
        path: "institution",
        lazy: createLazyPagesRoute((module) => module.InstitutionPage, { requiresAdmin: true }),
      },
      { path: "user", lazy: createLazyPagesRoute((module) => module.UserPage, { requiresAdmin: true }) },
      {
        path: "subscription",
        lazy: createLazyPagesRoute((module) => module.SubscriptionPage, { requiresAdmin: true }),
      },
      { path: "indices", lazy: createLazyPagesRoute((module) => module.IndicesPage, { requiresAdmin: true }) },
      { path: "settings", lazy: createLazyPagesRoute((module) => module.SettingsPage, { requiresAdmin: true }) },
      { path: "*", lazy: createLazyPagesRoute((module) => module.NotFoundPage, { requiresAdmin: true }) },
    ],
  },
];

type LazyComponent = LazyRouteFunction<RouteObject>;

function createLazyPagesRoute(
  component: (module: typeof import("../pages")) => RouteComponent,
  options?: RouteProtectionOptions,
): LazyComponent {
  return createLazyRoute(
    import("../pages").then((module) => ({ default: component(module) })),
    options,
  );
}

function createLazyRoute(
  importFunc: Promise<{ default: RouteComponent }>,
  options?: RouteProtectionOptions,
): LazyComponent {
  return async () => {
    try {
      const Component = await importFunc;
      const ComponentFn = Component.default;

      return ComponentFn.isUnprotected
        ? {
            element: <ComponentFn />,
          }
        : {
            element: (
              <SecureRouteWrapper
                element={<ComponentFn />}
                requiresAdmin={options?.requiresAdmin}
                requiresInstitutionAdmin={options?.requiresInstitutionAdmin}
              />
            ),
          };
    } catch (error) {
      handleDynamicImportError(error);
      console.error("Error loading component", error);
      return {
        element: <ErrorPage />,
      };
    }
  };
}
